import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  EmptyState,
  Pagination,
  RowsPerPageType,
  RowsSkeletonLoader,
  Box as Wrapper,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { RequestError } from 'components/Errors/RequestError'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import { PageHeader } from 'components/header/PageHeader'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useWalletContext } from 'hooks/context/useWalletContext'
import { useChannelsQuery, useSupportedCurrenciesQuery } from 'hooks/queries'
import ChannelsTable from 'pages/ManageChannels/components/ChannelsTable'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { ChannelsHeaderButtons } from './components/ChannelsHeaderButtons'

export const ReceiveCrypto = () => {
  const { t } = useTranslation()
  const wallet = useWalletContext()
  const { data: currencies, isPending: isCurrenciesLoading } = useSupportedCurrenciesQuery()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPageType>(10)
  const [offset, setOffset] = useState(0)
  const {
    data: { channels = [], paging } = {},
    isFetching: isChannelsFetching,
    isError: isChannelsError,
    refetch: refetchChannels,
  } = useChannelsQuery({
    max: Number(rowsPerPage),
    offset,
    search: '',
    walletId: String(wallet?.lsid),
  })

  useEffect(() => {
    refetchChannels()
  }, [offset, rowsPerPage])

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.MANAGE_CHANNEL, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const isLoading = isChannelsFetching || isCurrenciesLoading

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
    const newOffset = newPage * Number(rowsPerPage)
    setOffset(newOffset)
  }

  const handleChangeRowsPerPage = (newRowsPerPage: RowsPerPageType) => {
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    setOffset(0)
  }

  return (
    <Wrapper className="channels-container">
      <PageHeader
        description={
          <Box>
            <h3 className="text-h3 text-foreground-prominent">
              {t('payments.channels.receiveCryptoTitle')}
            </h3>
            <p className="text-body-regular text-foreground-prominent">
              {t('payments.channels.receiveCryptoDescription')}
            </p>
          </Box>
        }
        renderRightSection={
          <ChannelsHeaderButtons
            refetchChannels={refetchChannels}
            secondaryAction
            showManageChannels={false}
          />
        }
      />

      {!channels?.length && isLoading && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {!channels?.length && !isLoading && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('noChannelsFound')}
          description={
            <>
              {t('noChannelsCreateChannel')}{' '}
              <ExternalLink href={process.env.CHANNELS_LEARN_MORE}>{t('learnMore')}</ExternalLink>
            </>
          }
        />
      )}

      {!!channels?.length && (
        <>
          {isLoading && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}
          {isChannelsError && (
            <Box className="mt-10">
              <RequestError />
            </Box>
          )}

          {!isLoading && !!channels?.length && !isChannelsError && (
            <>
              <ChannelsTable channels={channels} currencies={currencies} />
              {paging?.total > 10 && (
                <Pagination
                  testId="receive-crypto-pagination"
                  page={page}
                  rowsPerPage={rowsPerPage}
                  total={paging?.total ?? 0}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                />
              )}
            </>
          )}
        </>
      )}
    </Wrapper>
  )
}
